import React, { useEffect, useRef, useState } from "react";
import { VendorDepositSettings } from "../../components/growth/GrowthBits";
import { OnlinePhotos } from "../../components/vi/OnlinePhotos";
import { Upload, Plus, Save, X, AlertCircle, CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ReferralCard } from "../../components/social/ReferralCard";
import { VendorPageHits } from "../../components/vendor/VendorPageHits";
import { PromotePage } from "../../components/vendor/PromotePage";

const CATEGORIES = ["photographer","videographer","dj","band","caterer","venue","florist","planner","makeup","hair","transportation","officiant","cake","decor","stationery","other"];
const PRICE_RANGES = ["$","$$","$$$","$$$$"];

export default function VendorProfileEditor() {
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [serviceInput, setServiceInput] = useState("");
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);
  const fileRef = useRef();

  async function load() {
    const { data } = await api.get("/vendor/me");
    setV(data && data.vendor_id ? data : null);
    setForm(data && data.vendor_id ? data : {
      business_name: "", category: "photographer", city: "", state: "",
      description: "", services: [], price_range: "$$",
      contact_email: user?.email || "", contact_phone: "", website: "", instagram: "",
    });
  }
  async function loadConnect() {
    try {
      const { data } = await api.get("/vendor/connect/status");
      setConnect(data);
    } catch { setConnect({ connected: false }); }
  }
  useEffect(() => { load().catch(() => {}); loadConnect(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function startConnect() {
    setConnectBusy(true);
    try {
      const { data } = await api.post("/vendor/connect/onboard", {
        return_url: window.location.origin,
      });
      window.location.href = data.onboarding_url;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setConnectBusy(false); }
  }

  if (!form) return null;
  if (user?.role !== "vendor" && user?.role !== "admin") {
    return <div data-testid="vendor-not-allowed" className="text-center py-12"><AlertCircle className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" /><p className="font-serif text-2xl">Vendor account required</p><p className="text-[var(--muted)] mt-2">Update your role to "vendor" in settings to list your business.</p></div>;
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      if (v) {
        await api.patch(`/vendors/${v.vendor_id}`, form);
        toast.success("Profile updated");
      } else {
        await api.post("/vendors", form);
        toast.success("Profile created — pending admin approval");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  async function uploadPhoto(e) {
    if (!v) { toast.error("Save profile first"); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendors/${v.vendor_id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  }

  function addService() {
    if (!serviceInput) return;
    setForm({ ...form, services: [...(form.services || []), serviceInput] });
    setServiceInput("");
  }

  return (
    <div data-testid="vendor-editor">
      <VendorPageHits />
      <PromotePage />
      <div className="mb-6"><ReferralCard /></div>
      <VendorDepositSettings />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Vendor Profile</p>
          <h1 className="font-serif font-light text-4xl">Your marketplace listing</h1>
          {v && <p className="text-sm mt-2">Status: <span className={`text-xs uppercase tracking-wider px-2 py-1 rounded-full ${v.status === "approved" ? "bg-[#e9f3e7] text-[#2f6f2f]" : v.status === "featured" ? "bg-[var(--gold)] text-white" : "bg-[#fff2d4] text-[#7a5b13]"}`}>{v.status}</span></p>}
        </div>
        <button onClick={save} disabled={busy} data-testid="vendor-save" className="btn-gold"><Save className="w-4 h-4" /> {busy ? "Saving…" : v ? "Update" : "Create Profile"}</button>
      </div>

      <form onSubmit={save} className="grid lg:grid-cols-2 gap-6">
        {/* Stripe Connect — always visible so vendors can link payouts
            BEFORE saving a profile. Independent of `v` (existing profile). */}
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 lg:col-span-2" data-testid="stripe-connect-card">
          <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] flex items-center gap-2">
                <CreditCard className="w-3 h-3" /> Payouts (Stripe Connect)
              </p>
              <p className="text-sm text-[var(--muted)] mt-1 max-w-md">
                Link a Stripe account to receive direct payouts from couples who book you through the marketplace.
              </p>
            </div>
            {connect?.connected && connect?.charges_enabled && connect?.payouts_enabled ? (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-800">
                <Check className="w-3 h-3" /> Payouts enabled
              </span>
            ) : connect?.connected ? (
              <span className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-800">Onboarding incomplete</span>
            ) : (
              <span className="text-xs px-3 py-1.5 rounded-full bg-[var(--bg-warm)] text-[var(--muted)]">Not connected</span>
            )}
          </div>
          {connect?.connected && (
            <p className="text-[11px] text-[var(--muted)] font-mono mb-3">Account: {connect.stripe_account_id}</p>
          )}
          {connect?.requirements?.currently_due?.length > 0 && (
            <p className="text-[11px] text-amber-800 mb-3">Required: {connect.requirements.currently_due.slice(0, 4).join(", ")}</p>
          )}
          <button
            type="button"
            onClick={startConnect}
            disabled={connectBusy}
            data-testid="stripe-connect-btn"
            className="btn-gold text-xs"
          >
            {connectBusy ? "Redirecting…" :
              connect?.connected && connect?.payouts_enabled ? "Open Stripe dashboard" :
              connect?.connected ? "Continue onboarding" :
              "Connect with Stripe"}
          </button>
        </div>

        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 space-y-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Business</p>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Business Name</label><input data-testid="biz-name" required value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className="input-luxe" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Category</label><select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="input-luxe">{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Price Range</label><select value={form.price_range} onChange={(e) => setForm({...form, price_range: e.target.value})} className="input-luxe">{PRICE_RANGES.map(p => <option key={p}>{p}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">City</label><input required value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="input-luxe" /></div>
            <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">State</label><input value={form.state || ""} onChange={(e) => setForm({...form, state: e.target.value})} className="input-luxe" /></div>
          </div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">About</label><textarea required rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe resize-none" /></div>
        </div>

        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 space-y-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Contact</p>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Email</label><input type="email" required value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} className="input-luxe" /></div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Phone</label><input value={form.contact_phone || ""} onChange={(e) => setForm({...form, contact_phone: e.target.value})} className="input-luxe" /></div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Website</label><input value={form.website || ""} onChange={(e) => setForm({...form, website: e.target.value})} className="input-luxe" /></div>
          <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Instagram handle</label><input value={form.instagram || ""} onChange={(e) => setForm({...form, instagram: e.target.value})} className="input-luxe" placeholder="@yourhandle" /></div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">Services</label>
            <div className="flex gap-2 mb-2">
              <input value={serviceInput} onChange={(e) => setServiceInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addService(); }}} className="input-luxe flex-1" placeholder="e.g. Engagement shoot" />
              <button type="button" onClick={addService} className="btn-outline-gold py-2 px-4 text-xs">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">{form.services?.map((s, i) => (
              <span key={i} className="text-xs px-3 py-1 rounded-full bg-[var(--bg-warm)] flex items-center gap-1">{s}<button type="button" onClick={() => setForm({...form, services: form.services.filter((_, j) => j !== i)})}><X className="w-3 h-3" /></button></span>
            ))}</div>
          </div>
        </div>

        {v && (
          <div className="bg-white border border-[var(--line)] rounded-2xl p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Portfolio Photos</p>
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline-gold text-xs py-2"><Upload className="w-3 h-3" /> Upload</button>
              <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" />
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {v.photos?.map((p) => <img key={p} src={`${BACKEND_URL}/api/files/${p}`} alt="" className="rounded-lg aspect-square object-cover" />)}
              {v.photos?.length === 0 && <p className="col-span-full text-sm text-[var(--muted)] py-6 text-center">No photos yet — upload your portfolio.</p>}
            </div>
            <OnlinePhotos vendor={v} website={form.website} onAdded={load} />
          </div>
        )}
      </form>
    </div>
  );
}
